import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Phone, ArrowLeft, Sparkles, RefreshCw, Globe } from "lucide-react";
import { toast } from "sonner";

export default function PhoneLogin() {
  const { setUser } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [gender, setGender] = useState("female");
  const [referralCode, setReferralCode] = useState((params.get("ref") || "").toUpperCase());
  const [step, setStep] = useState("phone"); // phone | otp
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!referralCode) {
      const stored = localStorage.getItem("jj_referral_code");
      if (stored) setReferralCode(stored);
    }
  }, [referralCode]);

  // Countdown for resend cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const [provider, setProvider] = useState("Disabled");
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => {
        setProvider(data.phone_provider || "Disabled");
      })
      .catch(() => {});
  }, []);

  const sendCode = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) return toast.error(language === "hi" ? "10 अंकों का भारतीय मोबाइल नंबर दर्ज करें" : "Enter a 10-digit Indian mobile number");
    setBusy(true);
    try {
      if (provider === "Firebase") {
        const { sendOtp } = await import("@/lib/firebase");
        const confirmationResult = await sendOtp(`+91${digits}`);
        setConfirmation(confirmationResult);
      } else {
        await api.post("/auth/phone/send-otp", { phone: `+91${digits}` });
      }
      setStep("otp");
      setCooldown(60);
      toast.success(language === "hi" ? "ओटीपी +91 " + digits + " पर भेजा गया" : "OTP sent to +91 " + digits);
    } catch (e) {
      toast.error(formatError(e) || (language === "hi" ? "ओटीपी नहीं भेजा जा सका" : "Could not send OTP"));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0) return;
    const digits = phone.replace(/\D/g, "");
    setBusy(true);
    try {
      if (provider === "Firebase") {
        const { sendOtp } = await import("@/lib/firebase");
        const confirmationResult = await sendOtp(`+91${digits}`);
        setConfirmation(confirmationResult);
      } else {
        await api.post("/auth/phone/send-otp", { phone: `+91${digits}` });
      }
      setCooldown(60);
      toast.success(language === "hi" ? "ओटीपी दोबारा +91 " + digits + " पर भेजा गया" : "OTP resent to +91 " + digits);
    } catch (e) {
      toast.error(formatError(e) || (language === "hi" ? "ओटीपी दोबारा नहीं भेजा जा सका" : "Could not resend OTP"));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!code || code.length < 4 || code.length > 6) return toast.error(language === "hi" ? "कृपया 4 या 6 अंकों का कोड दर्ज करें" : "Enter the 4 or 6-digit code");
    const digits = phone.replace(/\D/g, "");
    setBusy(true);
    try {
      let resData;
      if (provider === "Firebase") {
        if (!confirmation) throw new Error(language === "hi" ? "कोई पुष्टि परिणाम नहीं। कोड दोबारा भेजने का प्रयास करें।" : "No confirmation result. Try resending the code.");
        const result = await confirmation.confirm(code);
        const token = await result.user.getIdToken();
        const { data } = await api.post("/auth/phone", {
          id_token: token,
          name,
          gender,
          referral_code: referralCode,
        });
        resData = data;
      } else {
        const { data } = await api.post("/auth/phone/verify-otp", {
          phone: `+91${digits}`,
          otp: code,
          name,
          gender,
          referral_code: referralCode,
        });
        resData = data;
      }
      if (resData.token) localStorage.setItem("jjodi_token", resData.token);
      setUser(resData.user);
      const isStaff = resData.user.role === "admin" || resData.user.role === "mitra" || resData.user.is_mitra;
      nav(isStaff ? "/app" : (resData.is_new || !resData.user.profile_complete ? "/onboarding" : "/app"));
    } catch (e) {
      toast.error(formatError(e) || (language === "hi" ? "सत्यापन विफल रहा" : "Verification failed"));
    } finally {
      setBusy(false);
    }
  };


  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/16846853/pexels-photo-16846853.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/40 to-transparent" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-secondary"/>
            <span className="font-heading text-2xl">Hamarsathi</span>
          </Link>
          <div>
            <h2 className="font-heading text-5xl leading-tight">
              {language === "hi" ? <>कोई पासवर्ड नहीं।<br/>बस आपका नंबर।</> : <>No password.<br/>Just your number.</>}
            </h2>
            <p className="mt-4 text-white/85 max-w-md">
              {language === "hi" ? "आपके फोन पर भेजे गए ओटीपी के साथ त्वरित और सुरक्षित साइन-इन।" : "Quick and secure sign-in with an OTP delivered to your phone."}
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/70">
            {language === "hi" ? "केवल भारतीय नंबर · +91" : "Indian numbers only · +91"}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute inset-0 mandala-bg pointer-events-none" />
        <div className="relative w-full max-w-md">
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary" data-testid="phone-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1"/>{language === "hi" ? "पीछे" : "Back"}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === "en" ? "hi" : "en")}
              className="rounded-full flex items-center gap-1.5 px-3 hover:bg-muted"
              data-testid="lang-switch-btn"
            >
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase">{language === "en" ? "हिं" : "EN"}</span>
            </Button>
          </div>
          <h1 className="font-heading text-4xl text-primary inline-flex items-center gap-2">
            <Phone className="w-7 h-7"/>
            {language === "hi" ? "फोन से साइन-इन" : "Phone Sign-in"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {language === "hi" ? "हम आपका नंबर सत्यापित करने के लिए एक बार का कोड भेजेंगे।" : "We'll send a one-time code to verify your number."}
          </p>

          {step === "phone" ? (
            <div className="mt-8 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  {language === "hi" ? "आपका नाम (नए खातों के लिए)" : "Your name (for new accounts)"}
                </Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={language === "hi" ? "पूरा नाम" : "Full name"} data-testid="phone-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  {language === "hi" ? "मैं हूँ" : "I am"}
                </Label>
                <RadioGroup value={gender} onValueChange={setGender} className="grid grid-cols-2 gap-3 mt-2">
                  {["female", "male"].map((g) => (
                    <div 
                      key={g} 
                      onClick={() => setGender(g)}
                      className={`cursor-pointer flex items-center gap-3 rounded-xl border p-3 ${gender === g ? "border-primary bg-accent" : "border-border hover:bg-slate-50"}`}
                    >
                      <RadioGroupItem value={g} id={`pg-${g}`} data-testid={`phone-gender-${g}`} onClick={(e) => e.stopPropagation()} />
                      <Label htmlFor={`pg-${g}`} className="capitalize text-sm cursor-pointer select-none">
                        {g === "female" 
                          ? (language === "hi" ? "दुल्हन / महिला" : "Bride / Woman") 
                          : (language === "hi" ? "दूल्हा / पुरुष" : "Groom / Man")}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  {language === "hi" ? "मोबाइल नंबर" : "Mobile number"}
                </Label>
                <div className="flex gap-2 mt-1.5">
                  <span className="inline-flex items-center px-3 rounded-md border border-border bg-muted text-sm font-medium">+91</span>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={10} placeholder="98765 43210" data-testid="phone-input" inputMode="numeric" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  {language === "hi" ? "रेफरल कोड (वैकल्पिक)" : "Referral Code (Optional)"}
                </Label>
                <Input value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="FRIENDCODE" className="mt-1.5 font-mono tracking-widest" data-testid="phone-referral" />
              </div>
              <Button onClick={sendCode} disabled={busy} className="w-full rounded-full h-11 btn-press" data-testid="phone-send-otp">
                {busy 
                  ? (language === "hi" ? "भेज रहा है..." : "Sending...") 
                  : (language === "hi" ? "ओटीपी भेजें" : "Send OTP")}
              </Button>

              <div className="relative my-5">
                <div className="ornament-divider" />
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full h-11 flex items-center justify-center gap-2"
                onClick={() => {
                  const googleClientId = "1024963400258-ka7ghj9i9hcdobbtrlmhsd87r0biqf71.apps.googleusercontent.com";
                  const redirectUri = encodeURIComponent("https://hamarsathi.com/user/social-login/callback/google");
                  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid%20email%20profile`;
                }}
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#EA4335" d="M12 5.04c1.7 0 3.2.6 4.4 1.8l3.3-3.3C17.7 1.6 15 1 12 1 7.2 1 3.2 3.8 1.4 8l3.9 3c1-3 3.8-6 6.7-6z"/><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.6h6.5c-.3 1.5-1.1 2.8-2.4 3.7l3.7 2.9c2.2-2 3.7-5 3.7-9z"/><path fill="#FBBC05" d="M5.3 14.8c-.3-.8-.4-1.8-.4-2.8s.1-2 .4-2.8L1.4 6.2C.5 8 .0 10 .0 12c0 2 .5 4 1.4 5.8l3.9-3z"/><path fill="#34A853" d="M12 23c3.2 0 6-1.1 8-3l-3.7-2.9c-1.1.7-2.5 1.2-4.3 1.2-3.4 0-6.2-2.3-7.2-5.4l-3.9 3C3.2 20.2 7.2 23 12 23z"/></svg>
                {language === "hi" ? "गूगल के साथ जारी रखें" : "Continue with Google"}
              </Button>
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  {language === "hi" ? "कोड दर्ज करें" : "Enter verification code"}
                </Label>
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g,""))} maxLength={6} placeholder="1234" inputMode="numeric" data-testid="phone-otp" className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono h-14" />
                <p className="text-xs text-muted-foreground mt-2">
                  {language === "hi" ? `कोड +91 ${phone} पर भेजा गया है` : `Code sent to +91 ${phone}`}
                </p>
              </div>
              <Button onClick={verify} disabled={busy} className="w-full rounded-full h-11 btn-press" data-testid="phone-verify">
                {busy 
                  ? (language === "hi" ? "सत्यापित कर रहा है..." : "Verifying...") 
                  : (language === "hi" ? "सत्यापित करें और आगे बढ़ें" : "Verify & continue")}
              </Button>
              <div className="flex gap-2">
                <Button onClick={() => { setStep("phone"); setCode(""); }} variant="ghost" className="flex-1" data-testid="phone-change">
                  {language === "hi" ? "नंबर बदलें" : "Change number"}
                </Button>
                <Button
                  onClick={resendCode}
                  disabled={busy || cooldown > 0}
                  variant="outline"
                  className="flex-1 gap-1"
                  data-testid="phone-resend"
                >
                  {cooldown > 0 
                    ? (language === "hi" ? `${cooldown} सेकंड में पुनः भेजें` : `Resend in ${cooldown}s`) 
                    : (<><RefreshCw className="w-3.5 h-3.5" /> {language === "hi" ? "दोबारा भेजें" : "Resend"}</>)}
                </Button>
              </div>
            </div>
          )}

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {language === "hi" ? "ईमेल पसंद करते हैं?" : "Prefer email?"}{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">
              {language === "hi" ? "ईमेल से साइन इन करें" : "Sign in with email"}
            </Link>
          </p>
        </div>
      </div>
      <div id="recaptcha-container"></div>
    </div>
  );
}
